import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Package } from "lucide-react";
import { SPICES, SEEDS, GRAINS, PREMIUM_SEEDS, IMAGES } from "@/lib/company";

function Card({ item, testid }) {
  return (
    <div data-testid={testid} className="product-card bg-white rounded-lg border border-neutral-200 overflow-hidden flex flex-col">
      <div className="aspect-[4/3] overflow-hidden bg-neutral-100">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <h3 className="font-display font-bold text-brand-charcoal text-lg">{item.name}</h3>
        <p className="mt-2 text-sm text-brand-charcoal/75 leading-relaxed flex-1">{item.description}</p>
        <Link to="/contact" data-testid={`inquire-${item.name.toLowerCase().replace(/\s+/g, "-")}`} className="mt-4 btn-outline-green text-sm self-start" style={{ padding: "0.6rem 1.1rem" }}>
          Inquire Now
        </Link>
      </div>
    </div>
  );
}

export default function SpicesSeedsGrains() {
  return (
    <div data-testid="spices-page">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.spices} alt="Spices and grains" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">Category B & C</span>
          <h1 className="font-display font-bold text-white text-3xl md:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Premium Indian Spices, Seeds & Grains —<br />
            <span className="text-brand-gold">Export Quality, Global Standards</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl">
            From the farms and spice markets of India to your production facility — sourced, inspected, and exported with the same rigorous quality controls we apply to our pharmaceutical extracts.
          </p>
        </div>
      </section>

      {/* Spices */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Category · Spices</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              Aromatic Indian Spices
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SPICES.map((s) => (
              <Card key={s.name} item={s} testid={`spice-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Seeds */}
      <section className="py-20 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Category · Seeds & Dried Fruits</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              Nutrient-Dense Seeds & Dried Fruits
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SEEDS.map((s) => (
              <Card key={s.name} item={s} testid={`seed-card-${s.name.toLowerCase().replace(/\s+/g, "-")}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Premium Export Seeds — Sesame & Psyllium (detailed spec pages) */}
      <section className="py-20 bg-white border-t border-neutral-100" data-testid="premium-seeds-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-14 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Premium Export Seeds</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              Compliance-Grade Seeds for<br />
              <span className="text-brand-green">EU · US · Australia Markets</span>
            </h2>
            <p className="mt-5 text-brand-charcoal/75 leading-relaxed">
              Two of India's flagship export categories — sortex-cleaned, steam-sterilized, and
              documented to the standards demanded by tahini majors, nutraceutical brands, and
              pharmaceutical formulators worldwide.
            </p>
          </div>

          <div className="space-y-16">
            {PREMIUM_SEEDS.map((p) => (
              <article
                key={p.slug}
                data-testid={`premium-seed-${p.slug}`}
                className="grid lg:grid-cols-12 gap-10 items-start"
              >
                {/* Image */}
                <div className="lg:col-span-5">
                  <div className="aspect-[4/5] bg-[#F1EDDE] rounded-lg overflow-hidden shadow-md">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] tracking-widest uppercase text-brand-gold font-bold border border-brand-gold/50 rounded-full px-2.5 py-1">
                      {p.tagline}
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div className="lg:col-span-7 space-y-6">
                  <div>
                    <h3 className="font-display font-bold text-brand-charcoal text-3xl md:text-4xl">
                      {p.name}
                    </h3>
                    <p className="mt-1 italic text-brand-charcoal/60">{p.botanical}</p>
                    <p className="mt-4 text-brand-charcoal/85 leading-relaxed">{p.intro}</p>
                  </div>

                  {/* Colors (only for sesame) */}
                  {p.colors && (
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-brand-gold mb-3">Available Colours</div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        {p.colors.map((c) => (
                          <div key={c.name} className="bg-[#F7F5EF] rounded-md p-3 border border-neutral-200">
                            <div className="font-bold text-brand-charcoal text-sm">{c.name}</div>
                            <div className="mt-1 text-xs text-brand-charcoal/70 leading-relaxed">{c.note}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Forms */}
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-brand-gold mb-3">Forms Available</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {p.forms.map((f, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-brand-charcoal">
                          <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Grades */}
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-brand-gold mb-3">Commercial Purity Grades</div>
                    <div className="space-y-2">
                      {p.grades.map((g, i) => (
                        <div key={i} className="flex items-center gap-4 bg-brand-cream rounded-md px-4 py-2.5 border border-neutral-200">
                          <div className="flex-1 text-sm text-brand-charcoal">{g.label}</div>
                          <div className="text-sm font-bold text-brand-green whitespace-nowrap">{g.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Specs + Compliance in 2 columns */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-brand-gold mb-3">Key Specifications</div>
                      <ul className="space-y-2">
                        {p.specs.map((s, i) => (
                          <li key={i} className="text-sm">
                            <span className="font-semibold text-brand-charcoal">{s.label}:</span>{" "}
                            <span className="text-brand-charcoal/75">{s.value}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-brand-gold mb-3 flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Compliance &amp; Certification
                      </div>
                      <ul className="space-y-1.5">
                        {p.compliance.map((c, i) => (
                          <li key={i} className="text-xs text-brand-charcoal/80 leading-relaxed">· {c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Order strip */}
                  <div className="bg-[#152018] rounded-md p-4 grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-[9px] font-bold tracking-widest uppercase text-brand-gold">Packaging</div>
                      <div className="mt-1 text-xs text-white font-semibold">{p.packaging}</div>
                    </div>
                    <div className="border-x border-white/10">
                      <div className="text-[9px] font-bold tracking-widest uppercase text-brand-gold">MOQ</div>
                      <div className="mt-1 text-xs text-white font-semibold">{p.moq}</div>
                    </div>
                    <div>
                      <div className="text-[9px] font-bold tracking-widest uppercase text-brand-gold">Lead Time</div>
                      <div className="mt-1 text-xs text-white font-semibold">{p.lead}</div>
                    </div>
                  </div>

                  <Link
                    to="/contact"
                    data-testid={`inquire-${p.slug}`}
                    className="btn-green inline-flex"
                    style={{ padding: "0.75rem 1.5rem" }}
                  >
                    <Package className="w-4 h-4" />
                    Request Quotation
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Grains */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Category · Grains</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              Indian Rice — Full Range
            </h2>
          </div>
          {GRAINS.map((g) => (
            <div
              key={g.name}
              data-testid={`grain-card-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="product-card bg-white rounded-lg border border-neutral-200 overflow-hidden grid lg:grid-cols-5 gap-0"
            >
              <div className="lg:col-span-3 bg-[#f5eddb] p-4 flex items-center justify-center">
                <img
                  src={g.image}
                  alt={g.name}
                  className="w-full h-auto max-h-[520px] object-contain"
                />
              </div>
              <div className="lg:col-span-2 p-8 md:p-10 flex flex-col justify-center">
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Grains</span>
                <h3 className="mt-2 font-display font-bold text-brand-charcoal text-2xl">{g.name}</h3>
                <p className="mt-4 text-sm text-brand-charcoal/80 leading-relaxed">{g.description}</p>
                <div className="mt-6">
                  <Link
                    to="/contact"
                    data-testid={`inquire-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="btn-green"
                    style={{ padding: "0.7rem 1.4rem", fontSize: "0.9rem" }}
                  >
                    Inquire Now
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quality note */}
      <section className="py-16 bg-brand-green text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="font-display font-bold text-2xl md:text-3xl">Quality Note</h3>
          <p className="mt-5 text-white/85 leading-relaxed">
            All spices, seeds, and grains are sourced from FSSAI-compliant suppliers, processed under hygienic conditions, and packed in moisture-proof packaging with full export documentation.
          </p>
          <Link to="/contact" data-testid="spices-cta-quote" className="mt-8 btn-gold inline-flex">Request a Quote</Link>
        </div>
      </section>
    </div>
  );
}
